import React, { useEffect, useRef, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Plus, Trash2, Edit3, Save, X, FolderTree as TreeIcon, FileType } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import SearchableSelect from "@/components/SearchableSelect";

const FIELD_TYPE_LABELS = { text: "Texte", date: "Date", number: "Nombre", contact: "Contact (Bedee)", societe: "Société (interne)", entreprise: "Entreprise (Bedee)" };

const blank = { name: "", description: "", template: "/{type}/{year}" };

export default function ArborescencesPanel() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const templateRef = useRef(null);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([
        api.get("/arborescences"),
        api.get("/metadata-catalog"),
      ]);
      setItems(a.data);
      setCatalog(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(blank); setEditing("new"); };
  const startEdit = (it) => {
    setForm({ name: it.name, description: it.description || "", template: it.template });
    setEditing(it.id);
  };

  const togglePlaceholder = (key) => {
    if (!key) return;
    const token = `{${key}}`;
    const input = templateRef.current;

    setForm((f) => {
      const current = f.template || "";

      if (current.includes(token)) {
        let next = current.split(token).join("");
        next = next.replace(/\/{2,}/g, "/");
        if (next.length > 1 && next.endsWith("/")) next = next.slice(0, -1);
        if (next === "") next = "/";
        toast.success(`${token} retiré.`);
        return { ...f, template: next };
      }

      // Cursor position : actif dans l'input → selectionStart, sinon fin de chaîne
      const pos =
        input && document.activeElement === input
          ? (input.selectionStart ?? current.length)
          : current.length;

      const before = current.slice(0, pos);
      const after = current.slice(pos);
      const sep = before !== "" && !before.endsWith("/") ? "/" : "";
      const sepAfter = after !== "" && !after.startsWith("/") ? "/" : "";
      const result = before + sep + token + sepAfter + after;
      const newCursor = pos + sep.length + token.length;

      setTimeout(() => {
        if (input) {
          input.focus();
          input.setSelectionRange(newCursor, newCursor);
        }
      }, 0);

      toast.success(`${token} inséré.`);
      return { ...f, template: result };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/arborescences", form);
        toast.success("Arborescence créée.");
      } else {
        await api.put(`/arborescences/${editing}`, form);
        toast.success("Arborescence mise à jour.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Supprimer "${it.name}" ?`)) return;
    try {
      await api.delete(`/arborescences/${it.id}`);
      toast.success("Supprimée.");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Standard placeholders always available
  const standardKeys = [
    { key: "type", label: "Type de document" },
    { key: "year", label: "Année (auto)" },
    { key: "month", label: "Mois (auto)" },
    { key: "date", label: "Date" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-6">
        <div className="text-sm text-zinc-600 max-w-2xl">
          Une <strong className="text-zinc-900">arborescence</strong> définit le chemin du dossier où les
          documents seront classés. Elle est composée de segments fixes (ex : « Contrats ») et de
          placeholders dynamiques issus du{" "}
          <Link to="/parametres?tab=metadata" className="text-[#002FA7] hover:underline">
            catalogue de métadonnées
          </Link>.
        </div>
        <button
          data-testid="arb-new-btn"
          onClick={startNew}
          className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-4 py-2 transition-colors shrink-0"
        >
          <Plus size={14} strokeWidth={1.5} /> Nouvelle arborescence
        </button>
      </div>

      {editing && (
        <form onSubmit={submit} className="border border-zinc-900 bg-white" data-testid="arb-editor">
          <div className="p-4 border-b border-zinc-900 bg-zinc-900 text-white flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono">
              {editing === "new" ? "Nouvelle arborescence" : "Édition"}
            </div>
            <button type="button" onClick={() => setEditing(null)} className="text-white/70 hover:text-white p-1">
              <X size={16} />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <FormRow label="Nom">
              <input
                data-testid="arb-name-input"
                required maxLength={120}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="ex : Par Client / Année"
                className="w-full border border-zinc-300 px-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              />
            </FormRow>
            <FormRow label="Description">
              <input
                data-testid="arb-desc-input"
                maxLength={500}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full border border-zinc-300 px-4 py-2 text-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              />
            </FormRow>
            <FormRow label="Chemin (template)">
              <input
                ref={templateRef}
                data-testid="arb-template-input"
                required maxLength={400}
                value={form.template}
                onChange={(e) => setForm({ ...form, template: e.target.value })}
                placeholder="/{type}/{year}"
                className="w-full border border-zinc-300 px-4 py-2 text-sm font-mono focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
              />
            </FormRow>

            <div className="pt-3 border-t border-zinc-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-3">
                Cliquez pour insérer / retirer un placeholder
              </div>
              <div className="space-y-3">
                <PlaceholderRow
                  title="Standards"
                  items={standardKeys.map((s) => ({ key: s.key, label: s.label, hint: `{${s.key}}` }))}
                  template={form.template}
                  onToggle={togglePlaceholder}
                />
                <PlaceholderRow
                  title="Métadonnées du catalogue"
                  items={catalog.map((c) => ({
                    key: c.key,
                    label: c.label,
                    hint: `${FIELD_TYPE_LABELS[c.field_type] || c.field_type}${c.format ? " · " + c.format : ""}`,
                  }))}
                  template={form.template}
                  onToggle={togglePlaceholder}
                  empty="Aucune métadonnée — créez-en dans l'onglet Métadonnées."
                />
              </div>
            </div>

            <div className="pt-3 border-t border-zinc-200">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">
                Aperçu
              </div>
              <PreviewTree template={form.template} />
            </div>
          </div>
          <div className="p-4 border-t border-zinc-200 bg-zinc-50/50 flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(null)} className="border border-zinc-300 text-sm px-4 py-2 hover:bg-zinc-100">
              Annuler
            </button>
            <button
              data-testid="arb-save-btn"
              type="submit"
              className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-medium px-4 py-2"
            >
              <Save size={14} /> Enregistrer
            </button>
          </div>
        </form>
      )}

      <div className="border border-zinc-200 bg-white" data-testid="arb-list">
        {loading ? (
          <div className="p-6 text-sm text-zinc-500">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <TreeIcon size={32} strokeWidth={1} className="text-zinc-300 mx-auto" />
            <div className="mt-3 text-sm text-zinc-500">Aucune arborescence — créez-en une.</div>
          </div>
        ) : items.map((it) => (
          <div key={it.id} className="p-5 border-b border-zinc-100 last:border-0 flex items-start justify-between gap-6" data-testid={`arb-card-${it.id}`}>
            <div className="min-w-0">
              <div className="text-base font-semibold text-zinc-900">{it.name}</div>
              {it.description && <div className="mt-1 text-sm text-zinc-600">{it.description}</div>}
              <div className="mt-2 text-xs font-mono text-[#002FA7] break-all">{it.template}</div>
              <div className="mt-2">
                <span className={`text-[10px] uppercase tracking-wider font-mono ${it.usage_count > 0 ? "text-[#002FA7]" : "text-zinc-400"}`}>
                  utilisée par {it.usage_count} type{it.usage_count > 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => startEdit(it)} className="text-zinc-500 hover:text-[#002FA7] p-2" data-testid={`arb-edit-${it.id}`}>
                <Edit3 size={16} strokeWidth={1.5} />
              </button>
              <button onClick={() => remove(it)} className="text-zinc-500 hover:text-[#DC2626] p-2" data-testid={`arb-delete-${it.id}`}>
                <Trash2 size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-mono mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function PlaceholderRow({ title, items, template, onToggle, empty }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono mb-1.5">{title}</div>
      <div className="flex flex-wrap gap-2">
        {items.length === 0 ? (
          <div className="text-xs text-zinc-400 italic">{empty}</div>
        ) : items.map((it) => {
          const token = `{${it.key}}`;
          const active = template.includes(token);
          return (
            <button
              key={it.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onToggle(it.key)}
              data-testid={`arb-toggle-${it.key}`}
              className={`inline-flex items-center gap-2 border text-xs px-3 py-1.5 transition-colors ${
                active
                  ? "border-[#002FA7] bg-[#002FA7] text-white hover:bg-[#00227A]"
                  : "border-zinc-300 bg-white text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7]"
              }`}
              title={it.hint}
            >
              <span className="font-mono text-[10px] opacity-70">{token}</span>
              <span>{it.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PreviewTree({ template }) {
  // Sample values for placeholders
  const samples = {
    type: "Contrat de travail", year: "2025", month: "03", date: "2025-03-15",
    societe: "ACME SAS", entreprise_1: "LEVINE KESZLER", entreprise_2: "CF PROFINA",
    contact_3: "Jean Dupont", reference: "REF-001",
    siren: "123456789", siret: "12345678901234", exercice: "2024", montant: "1234.56",
    description: "—", date_echeance: "2026-03-15",
  };
  const resolved = (template || "/").replace(/\{([a-z0-9_]+)\}/g, (_, k) => samples[k] || k);
  const parts = resolved.split("/").filter(Boolean);
  return (
    <div className="border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs">
      <div className="text-zinc-400">{template || "/"}</div>
      <div className="mt-2 space-y-0.5">
        <div>📁 /</div>
        {parts.map((p, i) => (
          <div key={i} style={{ paddingLeft: `${(i + 1) * 16}px` }}>
            <span className="text-zinc-400">└─</span> 📁 {p}
          </div>
        ))}
        <div style={{ paddingLeft: `${(parts.length + 1) * 16}px` }} className="text-zinc-500">
          <span className="text-zinc-400">└─</span> 📄 document.pdf
        </div>
      </div>
    </div>
  );
}
